import humps from "humps"

import {
  AnyEvent,
  AssignmentMessageFiltered,
  CallStateChangedMessage,
  ConversationStateChangedMessage,
  MetadataMessage,
  SnoozeMessage,
  SocketReconnect
} from "@doktor-se/bones-ui/dist/web-shared/types"

import { conversationsConnect, fetchReassignedConversations, reloadCalendarBookings, reloadCalendarSlots } from "api"
import { Thunk } from "lib/hooks"
import { queueOpeningHourUpdated, setBanner } from "reducers/app"
import { addPersonalBooking, removeBooking, updateBooking, updateBookingsAssignee } from "reducers/booking"
import {
  addMessage,
  messageRead,
  messageReadByPatient,
  metadata,
  patientComment,
  removeBookedAppointment,
  snooze,
  updateBookedAppointment,
  updateConversationState
} from "reducers/conversations/conversations.reducer"
import { setPatientStatus } from "reducers/patients/status.reducer"
import { setHasUnreadMessageInOutline } from "reducers/queue/queue.reducer"
import {
  clearUserStatus,
  profileCreated,
  profileRemoved,
  profileUpdated,
  staffAdded,
  staffRemoved,
  userStatus
} from "reducers/users/users.reducer"

import { hasProcessedMessage } from "./deduplication"
import { addPostponeTimer, handleAssignment } from "./handlers/handleAssignment"
import handleCall from "./handlers/handleCall"
import { handleCustomerMetadataUpdate } from "./handlers/handleCustomerMetadata"
import { handleNavbarNotifications } from "./handlers/handleNotifications"
import { handleProfile } from "./handlers/handleProfile"
import { snoozeTimer } from "./handlers/handleSnooze"
import { handleStaffLogout } from "./handlers/handleStaffLogout"

const handleEvents =
  (event: AnyEvent): Thunk =>
  (dispatch, getState) => {
    const eventData: AnyEvent = humps.camelizeKeys({ ...event }) as unknown as AnyEvent
    switch (eventData.event) {
      case "message":
        const { message } = eventData
        if (hasProcessedMessage(message?.id, message?.createdById)) {
          break
        }
        const { conversationId } = message
        if (message.type) {
          switch (message.type) {
            case "seen":
              // Update last seen based on who created the seen event
              if (!!conversationId) {
                const createdById = message.createdById
                const stateConversation = getState().conversations.conversations.entities[conversationId!]
                const patientId = stateConversation?.patientId
                const lastMessageId = stateConversation?.lastMessage?.id

                if (typeof lastMessageId === "number") {
                  if (createdById && patientId && createdById === patientId) {
                    dispatch(messageReadByPatient({ conversationId: conversationId!, messageId: lastMessageId }))
                  } else {
                    dispatch(messageRead({ conversationId: conversationId!, messageId: lastMessageId }))
                  }
                }
              }
              break
            case "snooze":
              if (!!conversationId) {
                dispatch(snoozeTimer(conversationId, (message as SnoozeMessage).data.snoozedUntil || undefined))
              }
              dispatch(snooze(message as SnoozeMessage))
              break
            case "call":
              dispatch(handleCall(message as CallStateChangedMessage))
              dispatch(handleNavbarNotifications(message))
              break
            case "assignment":
              const postponedUntil = (message as AssignmentMessageFiltered).data.postponedUntil
              if (!!postponedUntil && !!conversationId)
                dispatch(addPostponeTimer(conversationId, postponedUntil, message.data.queue))

              // Always apply assignment locally
              dispatch(handleAssignment(message as AssignmentMessageFiltered))

              const assignAction = (message as AssignmentMessageFiltered).data?.assignAction as string | undefined
              const createdById = (message as AssignmentMessageFiltered).createdById as string | undefined
              if (assignAction === "conversation_assign_from_queue" && createdById === getState().auth.user?.id) {
                dispatch(fetchReassignedConversations())
              }
              break
            case "state":
              dispatch(updateConversationState(message as ConversationStateChangedMessage))
              break
            case "metadata":
              dispatch(metadata(message as MetadataMessage))
              break
            case "profile":
              dispatch(handleNavbarNotifications(message))
              dispatch(handleProfile(message))
              break
            case "system":
            case "image":
            case "attachment":
            case "chat":
            case "sip":
            case "category":
            case "comment":
            case "revisit_created":
            case "button":
              dispatch(handleNavbarNotifications(message))
              dispatch(addMessage(message))
              break
            default:
              break
          }
        }
        break
      case "socket.open":
        dispatch(setBanner(undefined))
        break
      case "socket.disconnected":
        dispatch(setBanner({ type: "stop" }))
        break
      case "socket.failed":
        dispatch(setBanner({ type: "close" }))
        break
      case "socket.reconnect":
        dispatch(setBanner({ type: "reconnect", meta: { retry: (event as SocketReconnect).retry } }))
        dispatch(conversationsConnect())
        dispatch(clearUserStatus())
        break
      case "user.status":
        if (eventData.userGroup === "staff") dispatch(userStatus(eventData))
        break
      case "staff.added":
        dispatch(staffAdded(eventData))
        break
      case "staff.removed":
        dispatch(staffRemoved(eventData))
        break
      case "profile.created":
        dispatch(profileCreated(eventData))
        break
      case "profile.removed":
        dispatch(profileRemoved(eventData))
        break
      case "profile.updated":
        dispatch(profileUpdated(eventData))
        break
      case "patient.comment":
        dispatch(patientComment(eventData))
        break
      case "opening_hour.updated":
        dispatch(queueOpeningHourUpdated(eventData.openingHour))
        break
      case "booking.create":
        if (getState().auth.user?.id && eventData.booking.staffId === getState().auth.user?.id) {
          dispatch(addPersonalBooking({ booking: eventData.booking, userId: getState().auth.user?.id }))
        }
        dispatch(reloadCalendarBookings())
        dispatch(reloadCalendarSlots())
        break
      case "booking.assign":
        const selectedStaff = getState().booking.calendar.filters.staff
        if (!selectedStaff || selectedStaff === getState().auth.user?.id) {
          dispatch(reloadCalendarBookings())
          dispatch(reloadCalendarSlots())
        } else {
          dispatch(updateBookingsAssignee({ bookingIds: [eventData.booking.id], staffId: eventData.booking.staffId }))
        }

        // Assign to personal bookings if assigned to current user
        if (eventData.booking.staffId === getState().auth.user?.id) {
          dispatch(addPersonalBooking({ booking: eventData.booking, userId: getState().auth.user?.id }))
        }

        dispatch(
          updateBookedAppointment({
            conversationId: eventData.booking.conversationId,
            changes: { assignedStaffId: eventData.booking.staffId }
          })
        )
        break
      case "booking.cancel":
        dispatch(removeBooking(eventData.booking.id))
        dispatch(reloadCalendarSlots())
        dispatch(removeBookedAppointment(eventData.booking.conversationId))

        break
      case "booking.unassign":
        const staffFilter = getState().booking.calendar.filters.staff
        if (!staffFilter || staffFilter === getState().auth.user?.id) {
          dispatch(removeBooking(eventData.booking.id))
          dispatch(reloadCalendarSlots())
        } else {
          dispatch(updateBookingsAssignee({ bookingIds: [eventData.booking.id], staffId: undefined }))
        }
        dispatch(
          updateBookedAppointment({
            conversationId: eventData.booking.conversationId,
            changes: { assignedStaffId: undefined }
          })
        )
        break
      case "booking.note_update":
        dispatch(
          updateBookedAppointment({
            conversationId: eventData.booking.conversationId,
            changes: { bookingNote: eventData.booking.note }
          })
        )
        break
      case "booking.reschedule":
        dispatch(updateBooking(eventData.booking))
        dispatch(reloadCalendarSlots())
        dispatch(
          updateBookedAppointment({
            conversationId: eventData.booking.conversationId,
            changes: { bookingStart: eventData.booking.start }
          })
        )
        break
      case "customer_metadata.conversation.updated":
        dispatch(handleCustomerMetadataUpdate(eventData))
        break
      case "staff.logout":
        dispatch(handleStaffLogout(eventData))
        break
      case "conversation.unread":
        dispatch(setHasUnreadMessageInOutline({ conversationId: eventData.message.conversation }))
        break
      case "patient.status":
        dispatch(setPatientStatus({ patientId: eventData.patientId, status: eventData.status }))
        break
      default:
        break
    }
  }

export default handleEvents
